import React, { useState, useEffect } from "react";
import MarkdownContent from "../MarkdownContent/MarkdownContent";
import ChatSourcesPanel from "../ChatSourcesPanel/ChatSourcesPanel";
import MessageGraphs from "../MessageGraphs/MessageGraphs";
import { formatToolLabel, TP_TRANSCRIPT_TOOLS } from "../../utils/talentPulseUtils";
import "./TalentPulseStepDropdown.css";

export default function TalentPulseStepDropdown({ block, defaultExpanded = false, handleCitationDownload, isStreaming = false, charts = null, graphVariant = "compact" }) {
  const isProcessing = isStreaming && block.items.some((it) => it.status === "processing");
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || isProcessing);

  useEffect(() => {
    if (isProcessing) {
      setIsExpanded(true);
    } else if (!isStreaming) {
       setIsExpanded(false);
    }
  }, [isProcessing, isStreaming]);

  const toggleExpand = () => {
    setIsExpanded((prev) => !prev);
  };

  let title = "";

  if (block.type === "thinking_group") {
    const thinkingItem = block.items.find((it) => it.type === "thinking");
    const toolItems = block.items.filter((it) => it.type === "tool_result");

    if (toolItems.length > 0) {
      const toolLabels = toolItems
        .map((it) => formatToolLabel(it.tool) || it.label)
        .filter(Boolean);
      const uniqueLabels = Array.from(new Set(toolLabels));
      title = `Thinking & ${uniqueLabels.join(", ") || "Tool Execution"}`;
    } else {
      title = thinkingItem?.label || "Thinking";
    }
  } else {
    // standalone tool_result_group
    const toolItem = block.items[0];
    title = formatToolLabel(toolItem?.tool) || toolItem?.label || "Internal Data";
  }

  /**
   * Build ChatSourcesPanel props from a tool_result step's citations array.
   */
  const buildCitationPanel = (item) => {
    const rawCitations = (Array.isArray(item.citations) && item.citations.length > 0 ? item.citations : item.sources) || [];
    if (!Array.isArray(rawCitations) || rawCitations.length === 0) return null;

    const toolName = item.tool || "";
    const isTranscript = !!toolName && TP_TRANSCRIPT_TOOLS.has(toolName.toLowerCase());
    const type = isTranscript ? "transcript" : "web";

    const sources = rawCitations
      .map((c) => ({
        label: c.title || c.filename || c.file_name || c.text || "",
        href: c.url || c.uri || "#",
        fileId: c.file_id || "",
        fileName: c.file_name || c.filename || c.title || "",
        sessionId: c.session_id || c.sessionId || item.sessionId || item.session_id || block.sessionId || block.session_id || "",
        date: c.interview_date || c.date || c.refresh_date || c.retrieved_date || c.last_modified || c.updated_at || c.timestamp || "",
      }))
      .filter((s) => s.label);

    if (!sources.length) return null;

    return (
      <ChatSourcesPanel
        type={type}
        sources={sources}
        onItemClick={
          isTranscript && handleCitationDownload
            ? (src) => handleCitationDownload(src.sessionId, src.fileId, src.fileName)
            : undefined
        }
      />
    );
  };

  return (
    <div className={`tp-step-dropdown ${isExpanded ? "expanded" : "collapsed"} ${isProcessing ? "is-processing" : ""}`}>
      <div className="tp-step-dropdown-header" onClick={toggleExpand}>
        <span className={`tp-dropdown-arrow ${isExpanded ? "open" : ""}`}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
        <div className="tp-step-dropdown-title-col">
          <span className="tp-step-dropdown-title">{title}</span>
          {isProcessing && (
            <span className="tp-step-spinner" title="Step running...">
              <svg className="tp-spinner-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle className="tp-spinner-track" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
                <path className="tp-spinner-head" d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </span>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="tp-step-dropdown-body">
          {block.items.map((item, idx) => {
            const itemProcessing = isStreaming && item.status === "processing";
            if (item.type === "thinking") {
              const hasText = typeof item.content === "string" && item.content.trim().length > 0;
              if (!hasText && !itemProcessing) {
                return null;
              }
              return (
                <div key={idx} className={`tp-thinking-content-box ${itemProcessing ? "item-processing" : ""}`}>
                  <div className="tp-thinking-label-row">
                    <span className="tp-thinking-label">Thought Process</span>
                    {itemProcessing && (
                      <span className="tp-inline-spinner" title="Thinking...">
                        <svg className="tp-spinner-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle className="tp-spinner-track" cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
                          <path className="tp-spinner-head" d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                        </svg>
                      </span>
                    )}
                  </div>
                  {hasText && (
                    <div className="tp-thinking-text">
                      <MarkdownContent content={item.content} useMathJax={false} />
                    </div>
                  )}
                </div>
              );
            }
            const SHOW_GRAPH = false;

            if (item.type === "tool_result") {
              const toolLabel = formatToolLabel(item.tool) || item.label || "Internal Data";
              const hasText = typeof item.content === "string" && item.content.trim().length > 0;
              const hasCitations = (Array.isArray(item.citations) && item.citations.length > 0) || (Array.isArray(item.sources) && item.sources.length > 0);
              const hasGraph = SHOW_GRAPH && !!(item.graph_json || item.graph);
              const isChartTool = (item.tool || "").toLowerCase() === "render_chart" || (item.tool || "").toLowerCase() === "chart";

              const CHART_RE = /\{\{CHART_[A-Za-z0-9_]+\}\}/g;
              const strippedContent = hasText ? item.content.replace(CHART_RE, "").trim() : "";
              const hasStrippedText = strippedContent.length > 0;

              if (!hasStrippedText && !hasCitations && !hasGraph && !(isChartTool && (!!item.graph || !!item.graph_json || (charts && Object.keys(charts).length > 0)))) {
                return null;
              }

              let itemGraph = null;
              if (item.graph_json || item.graph) {
                try {
                  const rawGraph = item.graph_json || item.graph;
                  itemGraph = typeof rawGraph === "string" ? JSON.parse(rawGraph) : rawGraph;
                } catch (e) { }
              } else if (isChartTool && charts && Object.keys(charts).length > 0) {
                itemGraph = Object.values(charts)[0];
              }

              const citationPanel = buildCitationPanel(item);
              return (
                <div key={idx} className={`tp-tool-result-box ${itemProcessing ? "item-processing" : ""}`}>
                  {toolLabel && (
                    <div className="tp-tool-result-header">
                      <span className="tp-tool-badge">{toolLabel}</span>
                    </div>
                  )}
                  {hasStrippedText && (
                    <div className="tp-tool-result-content">
                      <MarkdownContent content={strippedContent} useMathJax={false} />
                    </div>
                  )}
                  {itemGraph && (
                    <div className="tp-step-graph">
                      <MessageGraphs graph={itemGraph} variant="compact" />
                    </div>
                  )}
                  {citationPanel && (
                    <div className="tp-step-citations">
                      {citationPanel}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
}
